import {
  createContext,
  useContext,
  useMemo,
  useCallback,
  useState,
  PropsWithChildren,
  useEffect,
} from 'react';
import { Context } from './types';
import { useBoolState } from 'common/hooks';
import { EMPTY_STRING, StatusLoading } from 'common/constants';
import { useOperations as useFuelCardsOperations } from 'store/features/fuelCards';
import {
  isFuelCardsDataLoaded,
  useActiveFuelCard,
  useFuelCards,
} from 'store/features/fuelCards/selectors/fuelCards';
import { FuelCard } from 'dataSources/Typicode/fuelCards';
import { URL_PARAMS } from 'common/constants/url';
import resetURLParams from 'common/utils/url/resetURLParams';
import { useOperations as useStationsOperations } from 'store/features/stations';
import dataSources from 'dataSources';
import {
  useFilter,
  useRouteFilter,
} from 'store/features/stations/selectors/filter';
import { useRoutesContext } from 'common/context/routes';
import useUpdateActiveStationsByActiveRoute from 'pages/Map/hooks/useUpdateActiveStationsByActiveRoute';
import { SearchStation } from 'dataSources/Typicode/stations';
import addURLParams from 'common/utils/url/addURLParams';
import { useSelector } from 'react-redux';
import { selectors as stationsSelectors } from 'store/features/stations';
import { useActiveEvent } from 'store/features/events/selectors/activeEvent';
import { useFuelAvailabilityContext } from 'pages/Map/context/fuelAvailability';
import makeRestrictionsPayload from 'pages/Map/components/FuelAvailabilityButton/utils/makeRestrictionsPayload';

export const LOCAL_STORAGE_KEY = 'selectedFuelCard';

export const FuelCardsContext = createContext({} as Context);
export const useFuelCardsContext = () => useContext(FuelCardsContext);

export const FuelCardsProvider = ({ children }: PropsWithChildren) => {
  const [showFuelCardsModal, , , onHideFuelCardsModal, onShowFuelCardsModal] =
    useBoolState(false);
  const { setActiveFuelCard } = useFuelCardsOperations();
  const { updateAllStations, setAllStationsLoadingStatus } =
    useStationsOperations();
  const [searchValue, setSearchValue] = useState(EMPTY_STRING);
  const [selectedCard, setSelectedCard] = useState(EMPTY_STRING);
  const [cards, setCards] = useState<FuelCard[]>([]);
  const fuelCard = useActiveFuelCard();
  const fuelCards = useFuelCards();
  const isFuelCardsDataDone = isFuelCardsDataLoaded();
  const filterModalData = useFilter();
  const builderRouteFilterData = useRouteFilter();
  const { activeRoute } = useRoutesContext();
  const { fuelSelected, availabilitySelected, availabilityStatuses } =
    useFuelAvailabilityContext();
  const handleUpdateActiveRouteStations =
    useUpdateActiveStationsByActiveRoute();
  const { setStations } = useStationsOperations();
  const stationsMap = useSelector(stationsSelectors.allStationsMap);
  const activeEvent = useActiveEvent();

  useEffect(() => {
    if (!searchValue) {
      setCards(fuelCards);
    } else {
      const loweredValue = searchValue.toLowerCase();
      setCards(
        fuelCards.filter(({ fullNumberCard, auto, driverName }) => {
          return (
            fullNumberCard.includes(loweredValue) ||
            auto?.toLowerCase().includes(loweredValue) ||
            driverName?.toLowerCase().includes(loweredValue)
          );
        }),
      );
    }
  }, [searchValue]);

  useEffect(() => {
    if (isFuelCardsDataDone) {
      setCards(fuelCards);
    }
  }, [isFuelCardsDataDone]);

  const handleUpdateResults = useCallback(
    async (callback: (number: string) => void) => {
      setAllStationsLoadingStatus(StatusLoading.Load);
      setActiveFuelCard({ cardNumber: selectedCard });
      window.localStorage.setItem(LOCAL_STORAGE_KEY, selectedCard);
      resetURLParams([URL_PARAMS.fuelCardnumber]);

      if (selectedCard) {
        addURLParams({ [URL_PARAMS.fuelCardnumber]: selectedCard });
      }

      onHideFuelCardsModal();

      const filter = Object.keys(builderRouteFilterData).length
        ? builderRouteFilterData
        : filterModalData;
      const response = await dataSources.stations.getStationCodes({
        ...filter,
        ...(selectedCard ? { fullCardNumber: selectedCard } : {}),
        ...makeRestrictionsPayload({
          fuel: fuelSelected,
          availability: availabilitySelected,
          statuses: availabilityStatuses,
        }),
      });

      const data = response?.stations || [];

      const stationsData = data.reduce(
        (acc, { code, refuelingStatus }) => {
          acc[code] = { code, refuelingStatus };
          return acc;
        },
        {} as { [key: string]: SearchStation },
      );

      updateAllStations(stationsData);

      setAllStationsLoadingStatus(StatusLoading.Done);
      callback(selectedCard);
    },
    [
      activeEvent.data,
      stationsMap,
      selectedCard,
      setStations,
      setActiveFuelCard,
      filterModalData,
      builderRouteFilterData,
      activeRoute,
      handleUpdateActiveRouteStations,
      fuelSelected,
      availabilitySelected,
    ],
  );

  const handleSelectedCardChange = (cardNumber: string) => {
    setSelectedCard(cardNumber);
  };

  useEffect(() => {
    setSelectedCard(fuelCard);
  }, [fuelCard]);

  const value = useMemo(
    () => ({
      showFuelCardsModal,
      onHideFuelCardsModal,
      onShowFuelCardsModal,
      searchValue,
      setSearchValue,
      handleUpdateResults,
      selectedCard,
      setSelectedCard,
      cards,
      setCards,
      handleSelectedCardChange,
    }),
    [
      showFuelCardsModal,
      onHideFuelCardsModal,
      onShowFuelCardsModal,
      searchValue,
      setSearchValue,
      handleUpdateResults,
      selectedCard,
      setSelectedCard,
      cards,
      setCards,
      handleSelectedCardChange,
    ],
  );

  return (
    <FuelCardsContext.Provider value={value}>
      {children}
    </FuelCardsContext.Provider>
  );
};

export default FuelCardsProvider;
